import { FC, useMemo, useState } from 'react';

import { Link } from '@inertiajs/react';
import { ChevronDown, Store } from 'lucide-react';

import { HeaderUniverseMenu } from './HeaderUniverseMenu';
import { Routes } from '../../models/routes';
import { Expansion, Universe } from '../../types';
import { cn } from '../../utils';

type Props = {
    universes: Universe[];
    expansions: Expansion[];
    currentUniverse?: Universe | null;
    currentExpansion?: Expansion | null;
};

const MAX_VISIBLE_EXPANSIONS = 7;

const HeaderGamesNavigation: FC<Props> = ({
    universes,
    expansions,
    currentUniverse,
    currentExpansion,
}) => {
    const [menuOffsets, setMenuOffsets] = useState<Record<number, number>>({});
    const [openUniverseId, setOpenUniverseId] = useState<number | null>(null);

    const expansionsByUniverseSlug = useMemo(() => {
        const groupedExpansions = new Map<string, Expansion[]>();
        const seenTags = new Map<string, Set<string>>();

        expansions.forEach((expansion) => {
            const universeSlug = expansion.url_path.split('/')[1];
            if (!universeSlug) return;

            const expansionKey = expansion.tag || expansion.slug;
            const universeTags =
                seenTags.get(universeSlug) ?? new Set<string>();
            if (universeTags.has(expansionKey)) return;

            universeTags.add(expansionKey);
            seenTags.set(universeSlug, universeTags);

            const universeExpansions =
                groupedExpansions.get(universeSlug) ?? [];
            if (universeExpansions.length < MAX_VISIBLE_EXPANSIONS) {
                universeExpansions.push(expansion);
                groupedExpansions.set(universeSlug, universeExpansions);
            }
        });

        return groupedExpansions;
    }, [expansions]);

    const positionMenu = (
        universeId: number,
        alignRight: boolean,
        group: HTMLDivElement,
    ) => {
        const strip = group.closest<HTMLElement>(
            '[data-testid="header-games-strip"]',
        );
        const menu = group.querySelector<HTMLElement>(
            '[data-testid^="header-universe-menu-"]',
        );
        if (!strip || !menu) return;

        const stripBounds = strip.getBoundingClientRect();
        const groupBounds = group.getBoundingClientRect();
        const preferredLeft = alignRight
            ? groupBounds.right - stripBounds.left - menu.offsetWidth
            : groupBounds.left - stripBounds.left;
        const maxLeft = Math.max(0, stripBounds.width - menu.offsetWidth);
        const nextLeft = Math.min(maxLeft, Math.max(0, preferredLeft));

        setMenuOffsets((currentOffsets) =>
            currentOffsets[universeId] === nextLeft
                ? currentOffsets
                : { ...currentOffsets, [universeId]: nextLeft },
        );
    };

    const openMenu = (
        universeId: number,
        alignRight: boolean,
        group: HTMLDivElement,
    ) => {
        positionMenu(universeId, alignRight, group);
        setOpenUniverseId(universeId);
    };

    const closeMenu = (universeId: number) => {
        setOpenUniverseId((currentId) =>
            currentId === universeId ? null : currentId,
        );
    };

    return (
        <div className="hidden border-t md:block">
            <div
                className="relative container py-1.5"
                data-testid="header-games-strip"
            >
                <nav className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {universes.map((universe, index) => {
                        const universeExpansions =
                            expansionsByUniverseSlug.get(universe.slug) ?? [];
                        const alignRight =
                            index >= Math.ceil(universes.length / 2);
                        const isOpen = openUniverseId === universe.id;

                        return (
                            <div
                                key={universe.id}
                                data-testid={`header-universe-trigger-${universe.slug}`}
                                className="static -my-1.5 py-1.5"
                                onMouseEnter={({ currentTarget }) =>
                                    openMenu(
                                        universe.id,
                                        alignRight,
                                        currentTarget,
                                    )
                                }
                                onMouseLeave={() => closeMenu(universe.id)}
                                onFocus={({ currentTarget }) =>
                                    openMenu(
                                        universe.id,
                                        alignRight,
                                        currentTarget,
                                    )
                                }
                                onBlur={({ currentTarget, relatedTarget }) => {
                                    if (
                                        !currentTarget.contains(
                                            relatedTarget as Node | null,
                                        )
                                    ) {
                                        closeMenu(universe.id);
                                    }
                                }}
                            >
                                <Link
                                    href={universe.url_path}
                                    data-testid="header-universe-link"
                                    aria-haspopup="true"
                                    aria-expanded={isOpen}
                                    aria-controls={`header-universe-menu-${universe.id}`}
                                    onClick={() => closeMenu(universe.id)}
                                    className={cn(
                                        'flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm font-medium whitespace-nowrap transition-colors hover:text-primary',
                                        universe.id === currentUniverse?.id &&
                                            'text-primary',
                                    )}
                                >
                                    {universe.name}
                                    <ChevronDown
                                        className={cn(
                                            'h-3 w-3 transition-transform',
                                            isOpen && 'rotate-180',
                                        )}
                                    />
                                </Link>

                                <HeaderUniverseMenu
                                    universe={universe}
                                    expansions={universeExpansions}
                                    leftOffset={menuOffsets[universe.id]}
                                    open={isOpen}
                                    onNavigate={() => closeMenu(universe.id)}
                                    currentExpansionKey={
                                        currentExpansion?.tag ||
                                        currentExpansion?.slug
                                    }
                                />
                            </div>
                        );
                    })}

                    <Link
                        href={Routes.Sellers}
                        data-testid="header-sellers-link"
                        className="ml-auto flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-0.5 text-sm font-medium whitespace-nowrap transition-colors hover:text-primary"
                    >
                        <Store className="h-3.5 w-3.5" />
                        Продавці
                    </Link>
                </nav>
            </div>
        </div>
    );
};

export default HeaderGamesNavigation;
